import { useNavigate, useSearch } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { HomeCityField } from "~/components/HomeCityField";
import { OpenToBadges } from "~/components/OpenToBadges";
import { ProjectCard } from "~/components/ProjectCard";
import { SkillChips } from "~/components/SkillChips";
import {
  fetchMeetupMe,
  fetchMemberProfile,
  fetchMyProjects,
  fetchSkills,
  updateMeetupHomeCity,
  updateProject,
} from "~/lib/api";

export function MePage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const queryClient = useQueryClient();
  const fromEvent = () => (search() as { fromEvent?: string }).fromEvent ?? "";

  const meetupMeQuery = createQuery(() => ({
    queryKey: ["meetup-me"],
    queryFn: fetchMeetupMe,
  }));
  const homeCityMutation = createMutation(() => ({
    mutationFn: (city: string) => updateMeetupHomeCity(city),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meetup-me"] }),
  }));
  const profileQuery = createQuery(() => ({
    queryKey: ["member-profile"],
    queryFn: fetchMemberProfile,
  }));
  const projectsQuery = createQuery(() => ({
    queryKey: ["my-projects"],
    queryFn: fetchMyProjects,
  }));
  const skillsQuery = createQuery(() => ({
    queryKey: ["skills"],
    queryFn: fetchSkills,
  }));

  const pinMutation = createMutation(() => ({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      updateProject(id, { is_pinned: pinned }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-projects"] }),
  }));

  const profile = () => profileQuery.data?.data;
  const projects = () => projectsQuery.data?.data ?? [];
  const skillLookup = () => {
    const map = new Map<string, { id: string; slug: string; label: string; category: string }>();
    for (const tag of skillsQuery.data?.data ?? []) {
      map.set(tag.id, tag);
    }
    return map;
  };
  const profileSkills = () => {
    const lookup = skillLookup();
    return (profile()?.skill_tag_ids ?? [])
      .map((id) => lookup.get(id))
      .filter((tag): tag is NonNullable<typeof tag> => !!tag);
  };

  function goEdit() {
    navigate({
      to: "/me/edit",
      search: fromEvent() ? { fromEvent: fromEvent() } : {},
    });
  }

  function goAdd() {
    navigate({ to: "/me/projects/new" });
  }

  return (
    <section class="page page--flush me-page">
      <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
        <p class="status-chip">Me</p>
        <h1>Your portfolio</h1>
        <p class="muted">Shared across Melbourne, Sydney, and future events.</p>
      </div>

      <HomeCityField
        value={meetupMeQuery.data?.data.home_city ?? ""}
        disabled={homeCityMutation.isPending || meetupMeQuery.isPending}
        hint="Sorts city nights on Meetups. Does not hide other cities."
        onChange={(city) => homeCityMutation.mutate(city)}
      />
      <Show when={homeCityMutation.isError}>
        <div class="error-box" style={{ margin: "12px 20px 0" }}>
          {(homeCityMutation.error as Error).message}
        </div>
      </Show>

      <Show when={profileQuery.isPending}>
        <p class="muted" style={{ padding: "0 20px" }}>
          Loading profile…
        </p>
      </Show>

      <Show when={profileQuery.data} keyed>
        {(res) => (
          <button type="button" class="profile-preview-card me-profile-card" onClick={goEdit}>
            <Avatar url={res.data?.avatar_url} name={res.data?.display_name} size="lg" />
            <div>
              <strong>{res.data?.display_name ?? "Add your name"}</strong>
              <p class="profile-preview-headline">{res.data?.headline ?? res.data?.job_role}</p>
              <p class="muted">{res.data?.company}</p>
              <SkillChips skills={profileSkills()} limit={4} />
              <OpenToBadges flags={res.data} />
              <span class="muted">Edit profile</span>
            </div>
          </button>
        )}
      </Show>

      <Show when={projectsQuery.isPending}>
        <p class="muted" style={{ padding: "0 20px" }}>
          Loading projects…
        </p>
      </Show>
      <Show when={projectsQuery.isError}>
        <div class="error-box" style={{ margin: "0 20px" }}>
          {(projectsQuery.error as Error).message}
        </div>
      </Show>

      <Show when={!projectsQuery.isPending && projects().length === 0}>
        <div class="empty-state" style={{ margin: "16px 20px" }}>
          <p>No projects yet — show the work you want to talk about at DEB.</p>
          <p class="muted">A title and a short summary is enough to start.</p>
        </div>
      </Show>

      <Show when={projects().length}>
        <ul class="project-list">
          <For each={projects()}>
            {(project) => (
              <li>
                <ProjectCard
                  project={project}
                  onClick={() =>
                    navigate({
                      to: "/me/projects/$projectId",
                      params: { projectId: project.id },
                    })
                  }
                  trailing={
                    <button
                      type="button"
                      class="project-card-pin"
                      onClick={(e) => {
                        e.stopPropagation();
                        pinMutation.mutate({ id: project.id, pinned: !project.is_pinned });
                      }}
                    >
                      {project.is_pinned ? "Unpin" : "Pin to profile"}
                    </button>
                  }
                />
              </li>
            )}
          </For>
        </ul>
      </Show>

      <div class="sticky-cta">
        <button type="button" class="btn btn-primary" onClick={goAdd}>
          {projects().length ? "+ Add project" : "+ Add your first project"}
        </button>
      </div>
    </section>
  );
}
